import { Component, inject, signal } from '@angular/core';
import { PwaService } from '../../../core/pwa/pwa.service';

@Component({
  selector: 'app-pwa-status',
  standalone: true,
  template: `
    @if (!online()) {
      <div class="banner banner--offline" role="status">You're offline. Saved pages stay available.</div>
    }
    @if (updateReady() || (canInstall() && !installDismissed())) {
      <div class="stack">
        @if (updateReady()) {
          <div class="banner banner--action" role="status">
            <span>A new version is ready.</span>
            <button type="button" (click)="reload()">Reload</button>
          </div>
        }
        @if (canInstall() && !installDismissed()) {
          <div class="banner banner--action">
            <span>Install AMC Portal on this device.</span>
            <span class="banner__actions">
              <button type="button" (click)="install()">Install</button>
              <button type="button" class="banner__close" (click)="dismissInstall()" aria-label="Close">
                ×
              </button>
            </span>
          </div>
        }
      </div>
    }
  `,
  styles: `
    :host {
      display: contents;
    }

    .stack {
      position: fixed;
      z-index: 50;
      left: max(0.75rem, env(safe-area-inset-left));
      right: max(0.75rem, env(safe-area-inset-right));
      bottom: max(0.75rem, env(safe-area-inset-bottom));
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .banner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem;
      padding: 0.75rem 1rem;
      border-radius: 0.75rem;
      font-size: 0.875rem;
      line-height: 1.4;
      box-shadow: 0 8px 24px rgb(16 24 40 / 16%);
    }

    .banner--offline {
      position: fixed;
      z-index: 50;
      top: max(0.75rem, env(safe-area-inset-top));
      left: max(0.75rem, env(safe-area-inset-left));
      right: max(0.75rem, env(safe-area-inset-right));
      color: #7a2e0e;
      background: #fffaeb;
    }

    .banner--action {
      color: #fff;
      background: #0f4c81;
    }

    .banner__actions {
      display: flex;
      flex: 0 0 auto;
      align-items: center;
      gap: 0.25rem;
    }

    button {
      min-height: 44px;
      padding: 0 0.9rem;
      border: 0;
      border-radius: 999px;
      background: #fff;
      color: #0f4c81;
      font-weight: 600;
      cursor: pointer;
    }

    .banner__close {
      display: grid;
      width: 44px;
      padding: 0;
      place-items: center;
      background: transparent;
      color: #fff;
      font-size: 1.5rem;
      line-height: 1;
    }

    @media (max-width: 767px) {
      .banner {
        flex-wrap: wrap;
      }
    }
  `,
})
export class PwaStatus {
  private readonly pwa = inject(PwaService);

  readonly online = this.pwa.online;
  readonly updateReady = this.pwa.updateReady;
  readonly canInstall = this.pwa.canInstall;
  readonly installDismissed = signal(false);

  install(): void {
    void this.pwa.install();
  }

  dismissInstall(): void {
    this.installDismissed.set(true);
  }

  reload(): void {
    void this.pwa.activateUpdate();
  }
}
